import React, { useEffect, useState } from 'react'
import { NextPage } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Box, Container, Flex, Grid, GridItem, Text } from '@chakra-ui/react'
import { useInView } from 'react-intersection-observer'
import { useGtmPage, rentOfferToPageMeta } from 'src/utils/gtm'
import { Head, LinkRel, MetaTags } from 'src/components/common'
import { NltOffer } from 'src/components/common/StructuredData'
import { wrapper } from 'src/redux'
import { getNltOfferData } from 'src/utils/getStaticProps'
import { H3Title } from 'src/uikit'
import { PAGE_CACHE_CONTROL } from 'server/constants'
import { getFormattedMonthYear, replaceDatePlaceholders } from 'src/utils/dateUtils'
import { stripQueryString } from 'src/utils/url'

const { formattedYear } = getFormattedMonthYear()
const NltServiceContainer = dynamic(() => import('src/containers/NltServiceContainer'))
const BeforeNltSectionContainer = dynamic(() => import('src/containers/Sections/BeforeNltContainer'))
const HowNltWorksSectionContainer = dynamic(() => import('src/containers/Sections/HowNltWorksContainer'))
const NltCustomizeOfferModalContainer = dynamic(
  () => import('src/containers/NltCustomizeOfferModalContainer'),
)
const StickyBarContainer = dynamic(() => import('src/containers/StickyBarContainer'))
const VehicleOptionalsContainer = dynamic(() => import('src/containers/VehicleOptionalsContainer'))
const VehicleDetailsContainer = dynamic(() => import('src/containers/VehicleDetailsContainer'))
const SimilarOffersList = dynamic(() => import('src/components/NltOffers/SimilarOffersList'))
const PartnershipBanner = dynamic(() => import('src/components/OfferDetails/PartnershipBanner'))
const Breadcrumbs = dynamic(() => import('src/components/Breadcrumbs'))
const OfferGalleryContainer = dynamic(() => import('src/containers/OfferGalleryContainer'))
const OfferDetailsContainer = dynamic(() => import('src/containers/OfferDetailsContainer'))

interface Props {
  offerData: OfferGlobal.RentOfferDetail
  similarOffers: { input: OfferGlobal.RentOfferItem; predicted: Array<OfferGlobal.RentOfferItem> }
  variationSearch: { contractLength; annualMileage; deposit }
  howNltWorksData: { title: string; text: string }
}

const NltOfferPage: NextPage<Props> = ({ offerData, similarOffers, variationSearch, howNltWorksData }) => {
  const router = useRouter()
  const pathname = stripQueryString(router.asPath)
  const { t } = useTranslation()

  const [similarOffersItems, setSimilarOffersItems] = useState([similarOffers.input])

  if (
    variationSearch &&
    variationSearch.contractLength &&
    variationSearch.annualMileage &&
    variationSearch.deposit
  ) {
    let { contractLength, annualMileage, deposit } = variationSearch
    contractLength = parseInt(contractLength)
    annualMileage = parseInt(annualMileage)
    deposit = parseInt(deposit)

    const newDefault = offerData.variations.find(
      variation =>
        variation.contractLength === contractLength &&
        variation.annualMileage === annualMileage &&
        variation.deposit === deposit,
    )

    if (newDefault) {
      const oldDefault = {
        id: offerData.id,
        contractLength: offerData.contractLength,
        annualMileage: offerData.annualMileage,
        deposit: offerData.deposit,
        monthlyRate: offerData.monthlyRate,
      }

      offerData.contractLength = newDefault.contractLength
      offerData.annualMileage = newDefault.annualMileage
      offerData.deposit = newDefault.deposit
      offerData.monthlyRate = newDefault.monthlyRate

      offerData.variations = offerData.variations.filter(
        variation =>
          variation.contractLength !== contractLength ||
          variation.annualMileage !== annualMileage ||
          variation.deposit !== deposit,
      )

      offerData.variations.push({ ...oldDefault })
    }
  }

  const customizeModalIsOpen = useSelector((state: Store.RootState) => state.nlt.customizeModalIsOpen)

  const [ref, inView] = useInView({
    threshold: 0.5,
  })

  useEffect(() => {
    const MATTIA_MIN_ZINDEX = 2147483640 // To change if we changes MattIA z-index

    const getMattiaElements = () =>
      Array.from(document.body.children).filter((el): el is HTMLElement => {
        if (!(el instanceof HTMLElement)) return false
        const zIndex = parseInt(window.getComputedStyle(el).zIndex, 10)
        return !Number.isNaN(zIndex) && zIndex >= MATTIA_MIN_ZINDEX
      })

    const hideElements = () => {
      getMattiaElements().forEach(el => {
        if (el.style.display !== 'none') el.style.display = 'none'
      })
    }

    let attributeObserver: MutationObserver | null = null
    const watchAttributes = () => {
      const targets = getMattiaElements()
      if (!targets.length) return
      attributeObserver?.disconnect()
      attributeObserver = new MutationObserver(hideElements)
      targets.forEach(el =>
        attributeObserver!.observe(el, { attributes: true, attributeFilter: ['style', 'class'] }),
      )
    }

    const onBodyChange = () => {
      hideElements()
      watchAttributes()
    }

    onBodyChange()
    const bodyObserver = new MutationObserver(onBodyChange)
    bodyObserver.observe(document.body, { childList: true })

    return () => {
      bodyObserver.disconnect()
      attributeObserver?.disconnect()
      getMattiaElements().forEach(el => {
        el.style.display = ''
      })
    }
  }, [])

  useEffect(() => {
    setSimilarOffersItems(similarOffers.predicted)
  }, [similarOffers])

  const crumbs = [
    {
      page: 'Home Page',
      href: '/',
      title: 'Home Page',
    },
    {
      page: 'Offerte Noleggio a Lungo Termine',
      href: '/noleggio-a-lungo-termine',
      title: 'Offerte Noleggio a Lungo Termine',
    },
    {
      page: `${offerData.vehicle.brand}`,
      href: `/noleggio-a-lungo-termine/${offerData.vehicle.brandCode}`,
      title: `${offerData.vehicle.brand}`,
    },
    {
      page: `${offerData.vehicle.model}`,
      href: `/noleggio-a-lungo-termine/${offerData.vehicle.brandCode}/${offerData.vehicle.modelCode}`,
      title: `${offerData.vehicle.model}`,
    },
    {
      page: `${offerData.code}`,
      href: pathname,
      title: `${offerData.code}`,
    },
  ]

  const formattedMetaDataTitle = replaceDatePlaceholders(offerData.metaDataTitle)
  const formattedMetaDataDescription = replaceDatePlaceholders(offerData.metaDataDescription)

  const pageTitle =
    formattedMetaDataTitle ||
    t('nltPage:landingModel.title', {
      brand: offerData.vehicle.brand,
      model: offerData.vehicle.model,
      description: offerData.vehicle.version,
      fuel: offerData.vehicle.engine.fuel,
      year: formattedYear,
    })

  useGtmPage(rentOfferToPageMeta(offerData, { path: router.asPath, pageTitle }))

  return (
    <>
      <style global jsx>{`
        #CookiebotWidget {
          z-index: 1 !important;
        }
      `}</style>
      <Head title={pageTitle} />

      <MetaTags
        meta={t(`nltPage:landingModel.meta`, {
          returnObjects: true,
          brand: offerData.vehicle.brand,
          model: offerData.vehicle.model,
          description: offerData.vehicle.version,
          fuel: offerData.vehicle.engine.fuel,
          price: offerData.monthlyRate,
          numMonths: offerData.contractLength,
          metaDataDescription: formattedMetaDataDescription,
          url: `https://www.carplanner.com${pathname}`,
        })}
      />
      <LinkRel rel="canonical" href={`https://www.carplanner.com${pathname}`} />
      <NltOffer offerData={offerData} />

      {offerData.partnership && <PartnershipBanner partner={offerData.partnership} />}

      <Container maxW="8xl" px={{ base: '1.375rem', md: null }} ref={ref}>
        <Flex display={{ base: 'none', md: 'flex' }}>
          <Breadcrumbs items={crumbs} />
        </Flex>
        <Box mb="8" display={{ base: 'block', md: 'none' }}>
          <OfferGalleryContainer images={offerData.images} badge={offerData.mainTag} />
          <OfferDetailsContainer offerData={offerData} />
        </Box>
        <Grid templateColumns="2fr 1fr" gap={4} display={{ base: 'none', md: 'grid' }}>
          <GridItem>
            <OfferGalleryContainer images={offerData.images} badge={offerData.mainTag} />
          </GridItem>
          <GridItem>
            <OfferDetailsContainer offerData={offerData} />
          </GridItem>
        </Grid>
      </Container>
      <NltServiceContainer title={t('productPage:services.title')} services={offerData.services} />
      {offerData.description && (
        <Box bg="baseWhite" pb="xxl6" mt="xxl6">
          <Container maxW="7xl">
            <H3Title>{t('productPage:services.offerDescription')}</H3Title>
            {/* `as="div"`: la description del CMS contiene <div>/<ul>, non
                annidabili nel <p> di default di Text (rompe l'idratazione) */}
            <Text
              as="div"
              textAlign="left"
              color="baseDark"
              fontFamily="Montserrat-Regular"
              fontSize="1rem"
              letterSpacing="0.2px"
              lineHeight="20.8px"
              dangerouslySetInnerHTML={{
                __html: offerData.description,
              }}
            />
          </Container>
        </Box>
      )}
      <VehicleDetailsContainer
        vehicle={offerData.vehicle}
        title={t('productPage:vehicleDetails.title', {
          value: `${offerData.vehicle.name} ${offerData.vehicle.version}`,
        })}
      />
      {offerData.features.length > 0 && <VehicleOptionalsContainer optionals={offerData.features} />}
      <HowNltWorksSectionContainer content={howNltWorksData} />
      <BeforeNltSectionContainer />

      {similarOffersItems && similarOffersItems.length ? (
        <SimilarOffersList
          title={t('productPage:similarOffersTitle')}
          action={t('indexPage:nlt_offers.action')}
          alt={t('indexPage:nlt_offers.alt')}
          bgColor="baseNeutral.100"
          href="/noleggio-a-lungo-termine"
          offers={similarOffersItems}
          numPerView={4}
        />
      ) : null}

      <StickyBarContainer offerData={offerData} visible={!inView} />
      {customizeModalIsOpen && <NltCustomizeOfferModalContainer offerData={offerData} />}
    </>
  )
}

export const getServerSideProps = wrapper.getServerSideProps(store => async ({ query, res }) => {
  const { landing: brand, code, contractLength, annualMileage, deposit } = query

  let variationSearch = {}

  if (contractLength && annualMileage && deposit) {
    variationSearch = { contractLength, annualMileage, deposit }
  }

  try {
    const props = await getNltOfferData(`${brand}`, `${code}`, store)
    const { updatedAt } = props.offerData

    res.setHeader('Last-Modified', new Date(updatedAt).toUTCString())
    res.setHeader('Cache-Control', PAGE_CACHE_CONTROL)

    return { props: { ...props, variationSearch } }
  } catch (errorData: any) {
    if (errorData && errorData.redirect) {
      const { redirectTo, statusCode } = errorData.redirect

      res.setHeader('location', `/noleggio-a-lungo-termine${redirectTo}`)
      res.statusCode = statusCode
      res.end()
    } else {
      return { notFound: true }
    }
  }
})

export default NltOfferPage
